import { useHistory } from "react-router-dom";
import { useTheme } from "styled-components";
import { FiArrowRight } from "react-icons/fi";

import { ICompetition } from "../../@types";
import {
  COMPETITION_PLACEHOLDER_BANNER,
  getCompetitionPhase,
  getCountdownLabel,
  getPrizeLabel,
  isEndingWithinDays,
  TCompetitionPhase,
} from "../../utils";
import {
  CardContainer,
  BannerWrapper,
  BannerImage,
  BannerScrim,
  StatusPill,
  CardBody,
  Title,
  MetaRow,
  PrizeAmount,
  TimeText,
  DotSep,
  FooterCta,
} from "./styledComponents";

interface CompetitionCardProps {
  competition: ICompetition;
}

const handleBannerError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  const img = e.currentTarget;
  if (img.src === COMPETITION_PLACEHOLDER_BANNER) return;
  img.src = COMPETITION_PLACEHOLDER_BANNER;
};

const STATUS_LABEL: Record<TCompetitionPhase, string> = {
  active: "LIVE",
  upcoming: "UPCOMING",
  ended: "ENDED",
};

export const CompetitionCard = ({ competition }: CompetitionCardProps) => {
  const history = useHistory();
  const theme = useTheme() as { colors: Record<string, string | undefined> };
  const phase = getCompetitionPhase(competition);
  const timeLabel = getCountdownLabel(competition, phase);
  const prizeLabel = getPrizeLabel(competition.prizes);
  const isEndingSoon = phase === "active" && isEndingWithinDays(competition, 3);

  const statusAccent =
    phase === "active"
      ? theme.colors.envy ?? "#4dbd94"
      : phase === "upcoming"
      ? theme.colors.caution ?? "#f59e0b"
      : theme.colors.shade400 ?? "#557077";

  const banner = competition.bannerGraphic || COMPETITION_PLACEHOLDER_BANNER;
  const ctaLabel =
    phase === "ended"
      ? "View results"
      : phase === "upcoming"
      ? "Get notified"
      : "Enter";

  return (
    <CardContainer
      sponsored={competition.sponsored}
      onClick={() => history.push(`/competitions/${competition._id}`)}
    >
      <BannerWrapper>
        <BannerImage
          src={banner}
          alt={competition.title}
          onError={handleBannerError}
        />
        <BannerScrim />
        <StatusPill accent={statusAccent}>{STATUS_LABEL[phase]}</StatusPill>
      </BannerWrapper>
      <CardBody>
        <Title>{competition.title}</Title>
        <MetaRow>
          <PrizeAmount sponsored={competition.sponsored}>
            {prizeLabel}
          </PrizeAmount>
          <DotSep />
          <TimeText ending={isEndingSoon}>{timeLabel}</TimeText>
          <FooterCta>
            {ctaLabel}
            <FiArrowRight size={12} />
          </FooterCta>
        </MetaRow>
      </CardBody>
    </CardContainer>
  );
};
